import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { GripVertical, Factory } from 'lucide-react';

function ProcessStepCard({
  step,
  productName,
  plannedQuantity,
  orderId,
  orderNumber,
  itemId,
  itemIndex,
  productCode,
  specification = '',
  completedQuantity = 0,
  remainingQuantity,
  isOverlay = false,
  isSubcontracted = false,
}) {
  const displayQty = remainingQuantity !== undefined
    ? remainingQuantity
    : plannedQuantity - completedQuantity;
  const draggableId = `unassigned-step-${orderId}-${itemId}-${step.sequence}`;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: draggableId,
    disabled: isSubcontracted || displayQty <= 0,
    data: {
      type: 'unassigned-step',
      orderId,
      orderNumber,
      itemId,
      itemIndex,
      productCode,
      productName,
      specification,
      plannedQuantity,
      displayQuantity: displayQty,
      stepSequence: step.sequence,
      stepName: step.name,
      standardTime: step.standard_time,
      completedQuantity,
      isSubcontracted,
      step,
    },
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  if (isSubcontracted) {
    return (
      <div
        ref={setNodeRef}
        className="p-2 mb-1.5 bg-gray-50 border border-gray-200 rounded-md opacity-60 cursor-not-allowed"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Factory className="h-3 w-3 text-gray-400" />
            <span className="text-xs font-medium text-gray-500">{step.name}</span>
          </div>
          <Badge variant="secondary" className="text-[10px] h-4 px-1">已外协</Badge>
        </div>
      </div>
    );
  }

  const isEmpty = displayQty <= 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        p-2 mb-1.5 border rounded-md text-xs
        transition-shadow hover:shadow-md
        ${isDragging || isOverlay ? 'shadow-lg ring-2 ring-blue-400 opacity-90' : ''}
        bg-white border-gray-200
        ${isEmpty ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}
      `}
    >
      <div className="flex items-center gap-1">
        <GripVertical className="h-3 w-3 opacity-50 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold truncate" title={step.name}>
              {step.name}
            </span>
            <span className="text-[10px] text-gray-400 shrink-0">{step.standard_time}分</span>
          </div>
          <div className="text-[10px] text-gray-500 truncate">{productName || productCode}</div>
          {specification && (
            <div className="text-[10px] text-amber-600 truncate">{specification}</div>
          )}
          <div className="text-[10px] text-gray-500">
            剩余: {displayQty} / {plannedQuantity}
            {isEmpty && <span className="text-red-400 ml-1">(已分完)</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessStepCard;
